import { useParams, Link, Navigate } from "react-router-dom";
import { getPost, posts } from "@/data/journal";
import { Reveal } from "@/components/motion/Reveal";
import EarlyAccessForm from "@/components/EarlyAccessForm";

// Inline markdown-style link parser: [label](/path) → <Link>
const LINK_RE = /\[([^\]]+)\]\(([^)]+)\)/g;
function renderInline(text) {
  const nodes = [];
  let lastIndex = 0;
  let match;
  let key = 0;
  while ((match = LINK_RE.exec(text)) !== null) {
    if (match.index > lastIndex) {
      nodes.push(text.slice(lastIndex, match.index));
    }
    const [, label, href] = match;
    const isExternal = /^https?:\/\//.test(href);
    if (isExternal) {
      nodes.push(
        <a key={`l-${key++}`} href={href} target="_blank" rel="noreferrer noopener" className="link-underline text-ink">
          {label}
        </a>
      );
    } else {
      nodes.push(
        <Link key={`l-${key++}`} to={href} className="link-underline text-ink">
          {label}
        </Link>
      );
    }
    lastIndex = LINK_RE.lastIndex;
  }
  if (lastIndex < text.length) nodes.push(text.slice(lastIndex));
  return nodes;
}

export default function JournalPost() {
  const { slug } = useParams();
  const post = getPost(slug);
  if (!post) return <Navigate to="/journal" replace />;
  const idx = posts.findIndex((p) => p.slug === slug);
  const prev = posts[(idx - 1 + posts.length) % posts.length];
  const next = posts[(idx + 1) % posts.length];

  return (
    <article data-testid="journal-post-page">
      <section data-testid="post-hero" className="bg-ivory">
        <div className="max-w-[1200px] mx-auto px-6 md:px-10 pt-32 md:pt-40 pb-10 text-center">
          <p className="font-mono-stamp text-brass">{post.number} · {post.date}</p>
          <h1 className="font-display leading-[1.08] mt-8" style={{ fontSize: "clamp(48px, 8vw, 120px)", fontWeight: 300 }}>
            {post.title.split(" ").slice(0, -1).join(" ")}{" "}
            <em className="italic">{post.title.split(" ").slice(-1)}</em>
          </h1>
          <p className="mt-6 font-display italic text-2xl md:text-3xl text-pencil">{post.dek}</p>
        </div>
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <div className="border border-sage overflow-hidden">
            <img src={post.hero + "?w=1600&q=85"} alt={post.title} className="w-full h-[480px] md:h-[640px] object-cover" />
          </div>
        </div>
      </section>

      <section data-testid="post-body" className="bg-ivory">
        <div className="max-w-[760px] mx-auto px-6 md:px-8 py-20 md:py-28">
          <Reveal>
            <div className="space-y-8 text-[17px] leading-[1.9]">
              {post.body.map((item, i) => {
                const isObj = typeof item === "object" && item !== null;
                const type = isObj ? item.type : "p";
                const text = isObj ? item.text : item;
                if (type === "h") {
                  return (
                    <h2
                      key={i}
                      className="font-display text-3xl md:text-4xl leading-[1.2] pt-6 mt-4"
                      style={{ fontWeight: 400 }}
                    >
                      {renderInline(text)}
                    </h2>
                  );
                }
                // Lede paragraph = first body item
                const isLede = i === 0;
                return (
                  <p
                    key={i}
                    className={
                      isLede
                        ? "text-xl md:text-2xl font-display leading-[1.4]"
                        : ""
                    }
                    style={isLede ? { fontWeight: 400 } : {}}
                  >
                    {renderInline(text)}
                  </p>
                );
              })}
            </div>
          </Reveal>
          <div className="mt-16 pt-10 border-t border-sage">
            <p className="font-display italic text-2xl">— Robin</p>
          </div>
        </div>
      </section>

      {/* Prev/Next */}
      <section data-testid="post-nav" className="bg-ivory border-t border-sage">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 py-16 grid md:grid-cols-2 gap-6">
          <Link to={`/journal/${prev.slug}`} data-testid="post-prev" className="group block border border-sage p-8 hover:bg-cream transition-colors">
            <p className="font-mono-stamp text-pencil">← Previous</p>
            <p className="mt-4 font-display text-2xl md:text-3xl" style={{ fontWeight: 400 }}>{prev.title}</p>
          </Link>
          <Link to={`/journal/${next.slug}`} data-testid="post-next" className="group block border border-sage p-8 hover:bg-cream transition-colors text-right">
            <p className="font-mono-stamp text-pencil">Next →</p>
            <p className="mt-4 font-display text-2xl md:text-3xl" style={{ fontWeight: 400 }}>{next.title}</p>
          </Link>
        </div>
      </section>

      <section data-testid="post-join" className="bg-cream">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 py-20 md:py-28 grid md:grid-cols-12 gap-10">
          <div className="md:col-span-6">
            <p className="eyebrow eyebrow-brass mb-3">Follow the Work</p>
            <h3 className="font-display text-4xl md:text-5xl leading-[1.12]" style={{ fontWeight: 300 }}>
              Read them <em className="italic">first.</em>
            </h3>
          </div>
          <div className="md:col-span-6 flex items-end">
            <EarlyAccessForm testidPrefix="post-join" source="journal-post" />
          </div>
        </div>
      </section>
    </article>
  );
}
